import { useMutation } from '@apollo/client';
import { languageCodeToLanguage, useRouter, useShowToast } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  ContactSendContactMessageMutation,
  ContactSendContactMessageMutationVariables,
} from '@gql/graphql';
import { ContactReasonTypeEnum } from '@gql/graphql';
import type { ContactFormValues } from '@presentation/components';
import { useCallback } from 'react';

export const CONTACT_SEND_CONTACT_MESSAGE = graphql(`
  mutation ContactSendContactMessage(
    $email: String!
    $language: String!
    $messageContent: String!
    $phone: String
    $lastName: String
    $firstName: String
    $contactReasonType: ContactReasonTypeEnum!
    $country: CountryCode
  ) {
    contactSupport(
      email: $email
      language: $language
      messageContent: $messageContent
      phone: $phone
      lastName: $lastName
      firstName: $firstName
      contactReasonType: $contactReasonType
      country: $country
    )
  }
`);

export const useSendContactMessage = () => {
  const { language, translate, infoFromCurrentPath, countryCode, setState } = useRouter();
  const { showToast } = useShowToast();

  const [send, { loading, error }] = useMutation<
    ContactSendContactMessageMutation,
    ContactSendContactMessageMutationVariables
  >(CONTACT_SEND_CONTACT_MESSAGE);

  const showSuccessToast = useCallback(() => {
    showToast(translate('common.toasts.emailSent'), { type: 'success', autoClose: 1500 });
  }, [showToast, translate]);

  const sendContactMessageFromForm = useCallback(
    async (values: ContactFormValues) => {
      const { email, firstName, lastName, messageContent, phone } = values;

      let messageFormatted = messageContent;
      const contactError = infoFromCurrentPath?.state?.contactError;
      if (contactError) {
        messageFormatted = `${messageContent}\n\nError:\n${contactError}`;
      }
      if (typeof navigator !== undefined) {
        const browser = navigator.userAgent;
        messageFormatted = `${messageFormatted}\n\n\n\nBrowser:${browser}\n\nCountry:${countryCode}\n\nLanguage:${language}`;
      }
      await send({
        variables: {
          email,
          firstName,
          lastName,
          messageContent: messageFormatted,
          phone,
          language,
          contactReasonType: ContactReasonTypeEnum.USER_CONTACT,
          country: countryCode,
        },
      });
      showSuccessToast();
      setState(undefined);
    },
    [
      countryCode,
      infoFromCurrentPath?.state?.contactError,
      language,
      send,
      setState,
      showSuccessToast,
    ],
  );

  return {
    sendContactMessageFromForm,
    sendContactMessageLoading: loading,
    sendContactMessageError: error,
  };
};
